import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { GraduationCap, Stethoscope, Users, BookOpen } from 'lucide-react';

const AboutPage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] }
  };

  const qualifications = [
    { abbr: 'MBBS', full: 'Bachelor of Medicine, Bachelor of Surgery' },
    { abbr: 'MS (Gen Surg)', full: 'Master of Surgery in General Surgery' },
    { abbr: 'MRCS', full: 'Member of the Royal College of Surgeons' },
    { abbr: 'MCh (Plast)', full: 'Master of Surgery in Plastic Surgery' },
    { abbr: 'FRCS (Plast)', full: 'Fellow of the Royal College of Surgeons in Plastic Surgery' },
  ];

  const philosophyPoints = [
    {
      icon: Stethoscope,
      title: 'Patient-First Approach',
      description: 'Every consultation begins with listening. Understanding your concerns, goals, and expectations forms the foundation of our care.'
    },
    {
      icon: BookOpen,
      title: 'Evidence-Based Practice',
      description: 'Treatments and recommendations are grounded in the latest medical evidence and proven surgical techniques, not trends.'
    },
    {
      icon: Users,
      title: 'Informed Decision Making',
      description: 'We believe in transparency. You will receive comprehensive information to make decisions that are right for you.'
    },
    {
      icon: GraduationCap,
      title: 'Continuous Learning',
      description: 'Committed to staying at the forefront of plastic surgery through ongoing education, research, and professional development.'
    }
  ];

  return (
    <div className="bg-clinic-bg" data-testid="about-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28" data-testid="about-hero">
        <div className="container-clinic">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="initial"
              animate="animate"
              variants={{
                animate: { transition: { staggerChildren: 0.1 } }
              }}
            >
              <motion.p variants={fadeInUp} className="text-caption mb-4">
                About
              </motion.p>
              <motion.h1 variants={fadeInUp} className="text-heading-1 text-clinic-charcoal mb-8">
                Mr Kavish<br />Maheshwari
              </motion.h1>
              <motion.p variants={fadeInUp} className="text-body-lg mb-4">
                Consultant Plastic, Reconstructive & Aesthetic Surgeon
              </motion.p>
              <motion.p variants={fadeInUp} className="text-body-lg">
                A plastic surgeon whose practice is shaped by years of experience 
                in reconstructive surgery, including complex breast reconstruction, 
                offering a refined and considered approach to aesthetic treatments. 
                His work is characterised by meticulous attention to detail and a 
                deep understanding of form, proportion, and balance.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-body-lg mt-6">
                He is known for delivering subtle, elegant results in breast and 
                body contouring procedures, with a strong emphasis on safety, 
                discretion, and individualised care. Each treatment is carefully 
                tailored to enhance natural features while respecting the unique 
                goals and expectations of every patient.
              </motion.p>
              <motion.p variants={fadeInUp} className="text-body-lg mt-6">
                His work is further informed by ongoing academic contributions 
                and publications, reflecting a commitment to evidence-based 
                practice and continual refinement of technique.
              </motion.p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
            >
              <div className="aspect-[4/5] bg-clinic-muted relative overflow-hidden">
                <img
                  src="https://customer-assets-wrfwihn1.emergentagent.net/job_0b41d704-a68a-4dc5-aa3c-437daaaec2be/artifacts/cb2eacce6ad89c50_DSC02601-183.webp"
                  alt="Mr Kavish Maheshwari - Consultant Plastic Surgeon"
                  className="w-full h-full object-cover object-top"
                  data-testid="about-hero-image"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Qualifications Section */}
      <section className="section-padding bg-white" data-testid="qualifications-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-caption mb-4">Credentials</p>
            <h2 className="text-heading-2 text-clinic-charcoal">
              Qualifications & Training
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {qualifications.map((qual, index) => (
              <motion.div
                key={qual.abbr}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="p-8 border border-clinic-border bg-clinic-bg"
                data-testid={`qualification-${index}`}
              >
                <h3 className="font-heading text-2xl text-clinic-charcoal mb-2">
                  {qual.abbr}
                </h3>
                <p className="text-body-lg text-sm">{qual.full}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding" data-testid="philosophy-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mb-16"
          >
            <p className="text-caption mb-4">Approach</p>
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">
              Philosophy of Care
            </h2>
            <p className="text-body-lg">
              The practice is built on principles of honesty, safety, and respect. 
              Surgery is a significant decision, and my role is to guide you through 
              that process with clarity and care—never pressure.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {philosophyPoints.map((point, index) => (
              <motion.div
                key={point.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex gap-6"
                data-testid={`philosophy-point-${index}`}
              >
                <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center border border-clinic-border">
                  <point.icon size={20} className="text-clinic-charcoal" />
                </div>
                <div>
                  <h3 className="text-heading-3 text-clinic-charcoal mb-3">{point.title}</h3>
                  <p className="text-body-lg">{point.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Expertise Areas */}
      <section className="section-padding bg-clinic-muted" data-testid="expertise-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <p className="text-caption mb-4">Specialisation</p>
            <h2 className="text-heading-2 text-clinic-charcoal">
              Areas of Expertise
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              'Body Contouring Surgery',
              'Breast Surgery',
              'Non-Surgical Aesthetics',
              'Skin Cancer Surgery',
              'Reconstructive Surgery',
              'Hand Surgery',
              'Minor Operations',
              'Scar Management',
              'Laser Treatments'
            ].map((area, index) => (
              <motion.div
                key={area}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.05 }}
                className="p-6 bg-white border border-clinic-border"
                data-testid={`expertise-area-${index}`}
              >
                <span className="font-body text-clinic-charcoal">{area}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding" data-testid="about-cta-section">
        <div className="container-clinic">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-2xl mx-auto text-center"
          >
            <h2 className="text-heading-2 text-clinic-charcoal mb-6">
              Ready to discuss your options?
            </h2>
            <p className="text-body-lg mb-10">
              Book a consultation to explore how we can help you achieve your goals 
              with safety and expertise.
            </p>
            <Link
              to="/contact"
              className="btn-primary"
              data-testid="about-cta-btn"
            >
              Book a Consultation
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
